/**
 * Version Action Menu Component
 *
 * Provides action buttons for version management including:
 * - Set as Current (with automatic rotation)
 * - Set as Previous
 * - Clear System Tag
 * - Edit custom tags
 * - Export to various formats (JSON, CSV, XLSX, Parquet)
 * - Delete version (with protection for system-tagged versions)
 */

import React from 'react';
import { Tags, Download, Trash2, FileJson, FileText, FileSpreadsheet, FileArchive, FileCode, GitBranch, XCircle } from 'lucide-react';
import type { VersionExportFormat } from '@loader/shared-types';
import { Button } from '@loader/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@loader/ui';

import { toast } from '@/lib/toast';
import type { Version } from '@/features/tables/types';
import { useUpdateVersionSystemTagMutation } from '@/features/tables/hooks';

interface VersionActionMenuProps {
  /** Version to perform actions on */
  version: Version;
  /** Table ID for cache invalidation */
  tableId: string;
  /** Whether this is the only version (disables delete) */
  isOnlyVersion?: boolean;
  /** Callback when tags are successfully updated */
  onTagsUpdated?: () => void;
  /** Callback to open edit tags dialog */
  onOpenEdit: (version: Version) => void;
  /** Callback to request deletion */
  onRequestDelete: (version: Version, isProtected: boolean, isOnlyVersion: boolean) => void;
  /** Callback to handle export */
  onExport: (version: Version, format: VersionExportFormat) => void;
}

/**
 * Version Action Menu Component
 *
 * Renders a row of action buttons for version management.
 * Handles system tag updates (current/previous/clear), custom tag editing,
 * export to multiple formats, and version deletion.
 */
export const VersionActionMenu: React.FC<VersionActionMenuProps> = ({
  version,
  tableId,
  isOnlyVersion = false,
  onTagsUpdated,
  onOpenEdit,
  onRequestDelete,
  onExport,
}) => {
  const hasSystemTag = version.system_tag !== null;
  const isCurrent = version.system_tag === 'current';
  const isPrevious = version.system_tag === 'previous';
  const isFailed = version.status === 'failed';

  // Use mutation hook for automatic cache invalidation
  const updateSystemTagMutation = useUpdateVersionSystemTagMutation(tableId);

  /**
   * Set version as Current
   * Backend handles rotation logic automatically
   */
  const handleSetAsCurrent = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateSystemTagMutation.mutateAsync({
        versionId: version.id,
        systemTag: 'current',
      });
      toast.version.setCurrent();
      onTagsUpdated?.();
    } catch (error) {
      toast.version.setCurrentFailed(error instanceof Error ? error.message : undefined);
    }
  };

  /**
   * Set version as Previous
   */
  const handleSetAsPrevious = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateSystemTagMutation.mutateAsync({
        versionId: version.id,
        systemTag: 'previous',
      });
      toast.version.setPrevious();
      onTagsUpdated?.();
    } catch (error) {
      toast.version.setPreviousFailed(error instanceof Error ? error.message : undefined);
    }
  };

  /**
   * Clear system tag from version
   */
  const handleClearSystemTag = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateSystemTagMutation.mutateAsync({
        versionId: version.id,
        systemTag: null,
      });
      toast.version.tagCleared();
      onTagsUpdated?.();
    } catch (error) {
      toast.version.tagClearFailed(error instanceof Error ? error.message : undefined);
    }
  };

  /**
   * Handle delete request - pass protection info to parent
   */
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRequestDelete(version, hasSystemTag, isOnlyVersion);
  };

  return (
    <div className="flex w-full justify-start">
      {/* Tags menu - system tags + custom tags in one dropdown */}
      {!isFailed && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="mr-2 h-8 w-8"
              onClick={(e) => e.stopPropagation()}
              title="Tags"
              aria-label="Tags"
            >
              <Tags className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={handleSetAsCurrent} disabled={isCurrent}>
              <GitBranch className="mr-2 h-4 w-4" />
              Set as Current
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleSetAsPrevious} disabled={isPrevious}>
              <GitBranch className="mr-2 h-4 w-4" />
              Set as Previous
            </DropdownMenuItem>
            {hasSystemTag && (
              <DropdownMenuItem onClick={handleClearSystemTag}>
                <XCircle className="mr-2 h-4 w-4" />
                Clear System Tag
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenEdit(version); }}>
              <Tags className="mr-2 h-4 w-4" />
              Edit Custom Tags
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Export Dropdown - hidden for failed versions */}
      {!isFailed && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="mr-2 h-8 w-8"
              onClick={(e) => e.stopPropagation()}
              title="Export"
              aria-label="Export"
            >
              <Download className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => onExport(version, 'json')}>
              <FileJson className="mr-2 h-4 w-4" />
              JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport(version, 'csv')}>
              <FileText className="mr-2 h-4 w-4" />
              CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport(version, 'xlsx')}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport(version, 'xml')}>
              <FileCode className="mr-2 h-4 w-4" />
              XML
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport(version, 'parquet')}>
              <FileArchive className="mr-2 h-4 w-4" />
              Parquet
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Delete Button - always enabled, parent handles protection logic */}
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={handleDelete}
        title={
          isOnlyVersion
            ? 'Cannot delete the only version'
            : hasSystemTag
              ? 'Cannot delete protected version'
              : 'Delete version'
        }
        aria-label="Delete version"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};
